import { ENABLE_BANNER_WIDTH_OPTIONS, MenuItemsIndexDictionary } from '@components/EmailSignaturev2/consts';
// types and consts
import {
  EmailSignatureSideEditorItems,
  EmailSignatureSizes,
  SideEditorNodeKey,
} from '@components/EmailSignaturev2/types';
import { FormSelector, FormSelectorGroup } from '@components/FormSelector/FormSelector';
import ImageEditor, {
  ImageEditorButton,
  ImageEditorButtons,
  ImageEditorFrame,
} from '@components/ImageEditor/ImageEditor';
import ImageSelector from '@components/ImageSelector/ImageSelector';
import Input from '@components/Input/Input';
import { SideEditorScrollType } from '@components/SideEditor/SideEditor';
// hooks and context
import { useSideEditorContext } from '@components/SideEditor/utils';
import Tooltip from '@components/Tooltip/Tooltip';
import { emailSignatureSideEditorServicesContext } from '@context/email-signature-services-context';
import { faQuestionCircle, faTrash } from '@fortawesome/pro-light-svg-icons';
import { faImage } from '@fortawesome/pro-regular-svg-icons';
import { faLock } from '@fortawesome/pro-solid-svg-icons';
// components
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useNode from '@hooks/use-node';
import { getURIFromURLFieldValue } from '@modules/card-helpers';
import { generateNewEmailSignatureCustomBannerId } from '@modules/email-signatures-helpers';
import Emitter from '@modules/event-emitter';
// utils
import { debounce } from '@modules/helpers';
import { type FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { Crop } from 'react-image-crop';

import EmailSignatureEditorImageViewMode from '../EmailSignatureEditorImageViewMode';
import EmailSignatureLockLayer from '../EmailSignatureLockLayer';
import {
  EmailSignatureEditorAddonsBox,
  EmailSignatureEditorAddonsBoxBody,
  EmailSignatureEditorAddonsBoxHeader,
  EmailSignatureEditorAddonsBoxHeaderContents,
  EmailSignatureEditorAddonsBoxHeaderDivider,
} from './EmailSignatureEditorAddonBox';
import styles from './EmailSignatureEditorAddons.module.scss';
import bannerStyles from './EmailSignatureEditorAddonsBannerBox.module.scss';

const EmailSignatureEditorAddonsBannerBox: FC<React.PropsWithChildren<unknown>> = () => {
  const {
    isSignatureLocked,
    handleCroppedBannerImageChange,
    handleUploadOriginalBannerImageUrl,
    originalBannerImageUrl,
    displayBannerInViewMode,
    handleChangeBannerViewMode,
    isInTransitionState,
    localCroppedBannerImageUrl,
    emailSignature,
    emailSignatureDispatch,
  } = useContext(emailSignatureSideEditorServicesContext);
  const { scrollIntoView, registerNode, panelIndex, activeTab } = useSideEditorContext();
  const [previouslyCroppedPrimaryImageUrl, setPreviouslyCroppedBannerImageUrl] = useState<string | null>(
    null,
  );
  const [previousCrop, setPreviousCrop] = useState<Crop>();
  const [bannerBox, bannerBoxRef] = useNode<HTMLDivElement>();

  const [externalLink, setExternalLink] = useState('');

  const debouncedShowDummyBanner = useRef(
    debounce(() => {
      Emitter.emit('SHOW_DUMMY_BANNER');
    }, 200),
  );

  const debouncedRemoveDummyBanner = useRef(
    debounce(() => {
      Emitter.emit('REMOVE_DUMMY_BANNER');
    }, 200),
  );

  useEffect(() => {
    if (!bannerBox) return;
    registerNode(SideEditorNodeKey.BANNER_BOX_NODE, {
      panelBelongTo: panelIndex,
      node: bannerBox,
    });
  }, [bannerBox, panelIndex, registerNode]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  useEffect(() => {
    const handleScrollToBanner = () => {
      if (isInTransitionState) {
        return;
      }
      scrollIntoView(SideEditorNodeKey.BANNER_BOX_NODE, SideEditorScrollType.CENTER);
    };
    Emitter.on('SCROLL_TO_BANNER', handleScrollToBanner);

    return () => {
      Emitter.off('SCROLL_TO_BANNER', handleScrollToBanner);
    };
  }, [isInTransitionState, panelIndex, scrollIntoView]);

  const handleImageEditing = useCallback(
    (imageReference: string, crop: Crop) => {
      if (!emailSignature.banner?.id) return;

      handleCroppedBannerImageChange(imageReference, crop, emailSignature.banner.id);
    },
    [emailSignature.banner?.id, handleCroppedBannerImageChange],
  );

  const handleAddBanner = () => {
    emailSignatureDispatch({
      type: 'ADD_CUSTOM_BANNER',
      value: { imageUrl: '', id: generateNewEmailSignatureCustomBannerId() },
    });
  };

  const handleDeleteBanner = () => {
    if (!emailSignature.banner) {
      return;
    }
    // there are several clean up steps when the user deletes a banner

    // 1. delete the banner object
    emailSignatureDispatch({ type: 'DELETE_CUSTOM_BANNER' });

    // 2. clean up the local cropped image and original image
    handleUploadOriginalBannerImageUrl(null, emailSignature.banner.id);
    handleCroppedBannerImageChange('', null, emailSignature.banner.id);

    // 4. Since the banner is deleted, the banner is no longer in view mode
    handleChangeBannerViewMode(false);

    // 5. Reset the crop and previous crop
    setPreviousCrop(undefined);

    // 6 Reset the previously cropped image
    setPreviouslyCroppedBannerImageUrl(null);
  };

  const handleChangeBannerOriginalImage = () => {
    if (!emailSignature.banner) {
      return;
    }
    // there are several steps when the user changes a banner's original image

    // 1. reset the current original image and the local cropped image
    handleUploadOriginalBannerImageUrl(null, emailSignature.banner.id);
    handleCroppedBannerImageChange('', null, emailSignature.banner.id);

    // 2. the banner is no longer in the view mode when the user changes the original image
    handleChangeBannerViewMode(false);

    // 3. Reset the crop and previous crop
    setPreviousCrop(undefined);

    // 4. Reset the previously cropped image
    setPreviouslyCroppedBannerImageUrl(null);
  };

  const handleStartEditingBannerImage = () => {
    if (!emailSignature.banner) {
      return;
    }
    handleChangeBannerViewMode(false);
    setPreviouslyCroppedBannerImageUrl(localCroppedBannerImageUrl || '');
    setPreviousCrop(emailSignature.banner?.crop || undefined);
  };

  const handleCancelCroppedImageChange = () => {
    if (!emailSignature.banner) {
      return;
    }

    if (previouslyCroppedPrimaryImageUrl) {
      handleChangeBannerViewMode(true);
      handleCroppedBannerImageChange(
        previouslyCroppedPrimaryImageUrl,
        previousCrop || null,
        emailSignature.banner.id,
      );
    } else {
      handleUploadOriginalBannerImageUrl(null, emailSignature.banner.id);
      emailSignatureDispatch({ type: 'DELETE_CUSTOM_BANNER' });
    }
  };

  const handleConfirmCroppedImage = () => {
    if (!emailSignature.banner) {
      return;
    }
    handleChangeBannerViewMode(true);
  };

  const handleUploadBannerImage = (imageReference: string) => {
    if (!emailSignature.banner) {
      return;
    }
    handleUploadOriginalBannerImageUrl(imageReference, emailSignature.banner.id);
    handleCroppedBannerImageChange(imageReference, null, emailSignature.banner.id);
  };

  const handleBannerSizeChange = (newBannerSize: string) => {
    emailSignatureDispatch({
      type: 'UPDATE_CUSTOM_BANNER_SIZE',
      value: newBannerSize as EmailSignatureSizes,
    });
  };

  const index = MenuItemsIndexDictionary[EmailSignatureSideEditorItems.BANNERS];
  const isSelected = index === activeTab;

  return (
    <EmailSignatureEditorAddonsBox ref={bannerBoxRef}>
      <EmailSignatureEditorAddonsBoxHeader>
        <EmailSignatureEditorAddonsBoxHeaderContents>
          <FontAwesomeIcon
            size="1x"
            icon={faImage}
            className={bannerStyles['addons-box-header-banner-icon']}
          />
          <span>Your custom banner</span>
          {!emailSignature.banner && (
            <Tooltip
              label={
                'A banner is an image that usually offers discounts, support or a way to recruit employees or contact your business.'
              }
              aria-label={'addon-banner-info'}
            >
              <span>
                <button
                  onMouseEnter={() => debouncedShowDummyBanner.current()}
                  onMouseLeave={() => debouncedRemoveDummyBanner.current()}
                  onFocus={() => debouncedShowDummyBanner.current()}
                  onBlur={() => debouncedRemoveDummyBanner.current()}
                  className={styles['addons-box-header-info-button']}
                >
                  <FontAwesomeIcon size="1x" icon={faQuestionCircle} />
                </button>
              </span>
            </Tooltip>
          )}
        </EmailSignatureEditorAddonsBoxHeaderContents>
        {!emailSignature.banner && (
          <Tooltip
            size="small"
            label="You cannot add a banner, please contact your admin if you have any questions"
            aria-label="lock explaination message"
            show={isSignatureLocked}
          >
            <button
              disabled={isSignatureLocked}
              onClick={handleAddBanner}
              className={styles['addons-box-header-add-button']}
              data-is-locked={isSignatureLocked}
            >
              {isSignatureLocked ? <FontAwesomeIcon icon={faLock} /> : 'Add'}
            </button>
          </Tooltip>
        )}

        {!!emailSignature.banner && !isSignatureLocked && (
          <button onClick={handleDeleteBanner} className={styles['addons-box-header-delete-button']}>
            <FontAwesomeIcon size="1x" icon={faTrash} />
          </button>
        )}
      </EmailSignatureEditorAddonsBoxHeader>
      {!!emailSignature.banner && <EmailSignatureEditorAddonsBoxHeaderDivider />}
      <EmailSignatureEditorAddonsBoxBody collapsed={!emailSignature.banner}>
        <div className={bannerStyles['addons-images-field']}>
          {!originalBannerImageUrl && !displayBannerInViewMode && (
            <div>
              <ImageSelector
                disabled={isSignatureLocked}
                sizeRecommendation={{ width: 1200, height: 300 }}
                onSelect={handleUploadBannerImage}
                maxDimension={1500}
              ></ImageSelector>
            </div>
          )}

          {originalBannerImageUrl && !displayBannerInViewMode && (
            <div className={bannerStyles['image-editor-framer-container']}>
              {/* We need to rerender ImageEditorFrame to avoid a bug with react-image-crop + reach tabs. When
                            you render the image // in a tab that is hidden react-image-crop miscalculates the
                             width of the image as 0 and doesn't display it. */}
              {isSelected && (
                <ImageEditorFrame>
                  <ImageEditor
                    image={originalBannerImageUrl}
                    onEdit={handleImageEditing}
                    crop={emailSignature.banner?.crop || undefined}
                  />
                  <ImageEditorButtons>
                    <ImageEditorButton onClick={handleCancelCroppedImageChange}>Cancel</ImageEditorButton>
                    <ImageEditorButton onClick={handleConfirmCroppedImage}>Confirm</ImageEditorButton>
                  </ImageEditorButtons>
                </ImageEditorFrame>
              )}
            </div>
          )}

          {displayBannerInViewMode && (
            <EmailSignatureEditorImageViewMode
              isLocked={isSignatureLocked}
              onEditImage={handleStartEditingBannerImage}
              onChangeImage={handleChangeBannerOriginalImage}
              imageSrc={emailSignature.banner?.imageUrl || ''}
            />
          )}

          {emailSignature.banner?.imageUrl && (
            <Input
              value={externalLink || emailSignature.banner.externalLink}
              onChange={(newValue: string) => {
                const uri = getURIFromURLFieldValue('link', newValue);

                setExternalLink(newValue);

                emailSignatureDispatch({
                  type: 'UPDATE_CUSTOM_BANNER_EXTERNAL_LINK',
                  value: uri ?? newValue,
                });
              }}
              label={'External link'}
              contained
              enableClearButton={!isSignatureLocked}
              hideAllErrorMessages
              disabled={isSignatureLocked}
            />
          )}
        </div>
        {emailSignature.banner?.imageUrl && ENABLE_BANNER_WIDTH_OPTIONS && (
          <div className={styles['addons-field']}>
            <div className={styles['addons-field-label']}>Size</div>
            <FormSelectorGroup
              className={styles['addons-form-selector-group']}
              selectedValue={emailSignature.banner?.size ?? EmailSignatureSizes.MEDIUM}
              size="sm"
              onChange={handleBannerSizeChange}
            >
              <div className={styles['addons-form-selector']}>
                <FormSelector disabled={isSignatureLocked} value={EmailSignatureSizes.SMALL}>
                  Small
                </FormSelector>
              </div>
              <div className={styles['addons-form-selector']}>
                <FormSelector disabled={isSignatureLocked} value={EmailSignatureSizes.MEDIUM}>
                  Medium
                </FormSelector>
              </div>
              <div className={styles['addons-form-selector']}>
                <FormSelector disabled={isSignatureLocked} value={EmailSignatureSizes.LARGE}>
                  Large
                </FormSelector>
              </div>
            </FormSelectorGroup>
          </div>
        )}

        {isSignatureLocked && (
          <EmailSignatureLockLayer message="You cannot edit the banner, please contact your admin if you have any questions" />
        )}
      </EmailSignatureEditorAddonsBoxBody>
    </EmailSignatureEditorAddonsBox>
  );
};

export default EmailSignatureEditorAddonsBannerBox;
